import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxItem,
  ComboboxSearchField,
  ComboboxTrigger,
  ComboboxTriggerLoading,
  ComboboxValue,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  InputMask,
  masks,
} from '@barfacil/barfacil-ui'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Check, ImageIcon, Trash, X } from 'lucide-react'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'

import { Product, Products } from '@/api/_schemas/management'
import { UpdateProductGeralDialog } from '@/app/panel/management/price-list/_components/update-product-geral-dialog'
import { UpdateProductTaxDialog } from '@/app/panel/management/price-list/_components/update-product-tax-dialog'
import { useUpdateProductContext } from '@/app/panel/management/price-list/_contexts/update-product-context'
import { usePriceListProductOptions } from '@/app/panel/management/price-list/_hooks/use-product-options'
import {
  FormComboProduct,
  FormComboProductSchema,
} from '@/app/panel/management/price-list/_schemas/combo-product-form'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatToNumber, getDefaultsFromZod, removeAccents } from '@/lib/utils'

type ComboProduct = NonNullable<Product['produto_combo_list']>[0]

type NewComboProductActionsProps = {
  onClear: () => void
}

type ComboProductActionsProps = {
  product?: Product
  onDeleteComboItem: () => void
}

type ShowImageProps = {
  name: string
  imagePath?: string
}

function NewComboProductActions({ onClear }: NewComboProductActionsProps) {
  const form = useFormContext<FormComboProduct>()

  function handleOnClear() {
    form.reset()
    onClear()
  }

  return (
    <div className="flex items-center justify-end gap-1 text-sky-500">
      <Button className="size-6" variant="ghost" size="icon" type="button">
        <X size={24} onClick={handleOnClear} />
      </Button>

      <Button
        className="size-6"
        variant="ghost"
        size="icon"
        type="submit"
        form="price-list-new-combo-product-form"
      >
        <Check className="text-sky-500" size={24} />
      </Button>
    </div>
  )
}

function ComboProductActions({
  product,
  onDeleteComboItem,
}: ComboProductActionsProps) {
  return (
    <div className="flex items-center justify-end gap-2 text-sky-500">
      {!!product && (
        <aside className="flex items-center gap-2">
          <UpdateProductTaxDialog product={product} />
          <UpdateProductGeralDialog product={product} />
        </aside>
      )}

      <Button
        className="size-6"
        variant="ghost"
        size="icon"
        onClick={onDeleteComboItem}
      >
        <Trash size={20} />
      </Button>
    </div>
  )
}

function ShowImage({ name, imagePath }: ShowImageProps) {
  return (
    <Avatar className="pointer-events-none size-16 rounded">
      <AvatarImage src={imagePath} alt={name} loading="lazy" />

      <AvatarFallback className="size-16 rounded bg-white">
        <ImageIcon className="text-gray-200" />
      </AvatarFallback>
    </Avatar>
  )
}

function PriceListNewComboProduct() {
  const [openComboboxProduct, setOpenComboboxProduct] = useState(false)
  const [productSearch, setProductSearch] = useState('')
  const [filteredProducts, setFilteredProducts] = useState<Products>([])

  const [currentImagePath, setCurrentImagePath] = useState<string | null>(null)

  const { priceListId } = useUpdateProductContext()

  const form = useFormContext<FormComboProduct>()

  const { data: productOptions, isLoading: isLoadingProductOptions } =
    usePriceListProductOptions(priceListId)

  function clearSelectedImage() {
    setCurrentImagePath(null)
    form.setValue('imagem_path', undefined)
  }

  function handleProductSearchChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const value = event.currentTarget.value

    setProductSearch(value)
    form.setValue('produto_nome', value, { shouldValidate: true })

    setFilteredProducts(
      productOptions?.filter(({ nome }) =>
        removeAccents(nome.toUpperCase()).includes(
          removeAccents(value.toUpperCase()),
        ),
      ) ?? [],
    )
  }

  function handleOnSelectProduct(
    id: number,
    value: string,
    imagePath: string | null,
  ) {
    setCurrentImagePath(imagePath)
    form.setValue('imagem_path', imagePath ?? undefined)
    form.setValue('produto', id.toString())
    form.setValue('produto_nome', value, { shouldValidate: true })
  }

  useEffect(() => {
    if (!form.formState.isSubmitSuccessful) return

    setCurrentImagePath(null)
  }, [form.formState.isSubmitSuccessful])

  return (
    <TableRow>
      <TableCell className="min-w-64 max-w-64">
        <FormField
          control={form.control}
          name="produto_nome"
          render={({ field }) => (
            <FormItem>
              <Combobox
                open={openComboboxProduct}
                onOpenChange={setOpenComboboxProduct}
                onValueChange={field.onChange}
                defaultValue={field.value}
                focusSelected={false}
                disabled={form.formState.isSubmitting}
              >
                <FormControl>
                  {isLoadingProductOptions ? (
                    <ComboboxTriggerLoading>
                      Informe o nome do produto
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger
                      id="add-combo-product-combobox"
                      form="price-list-new-combo-product-form"
                      onBlur={() => setProductSearch('')}
                    >
                      <ComboboxValue placeholder="Informe o nome do produto">
                        {field.value}
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={productSearch}
                    autoComplete="off"
                    loading={isLoadingProductOptions}
                    onChange={handleProductSearchChange}
                    onBlur={(e) => {
                      if (e.target.value) {
                        form.setValue('produto', undefined)
                        clearSelectedImage()
                      }
                    }}
                    onKeyDown={(e) => {
                      if (!['Escape', 'Enter'].includes(e.key)) return
                      if (e.key === 'Enter') setOpenComboboxProduct(false)
                      if (e.currentTarget.value) clearSelectedImage()
                    }}
                  />

                  <ComboboxGroup>
                    {(!productSearch ? productOptions : filteredProducts)?.map(
                      ({ cod_produto: id, nome, imagem_path: imagePath }) => (
                        <ComboboxItem
                          key={id}
                          value={nome}
                          onClick={() => {
                            handleOnSelectProduct(id, nome, imagePath)
                            setOpenComboboxProduct(false)
                          }}
                        >
                          {nome}
                        </ComboboxItem>
                      ),
                    )}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>

              <FormMessage className="absolute w-max" />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="w-44 min-w-44 max-w-44">
        <FormField
          control={form.control}
          name="qtde_produto"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Quantidade"
                  form="price-list-new-combo-product-form"
                  disabled={form.formState.isSubmitting}
                />
              </FormControl>

              <FormMessage className="absolute w-max" />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="w-32 min-w-32 max-w-32">
        <FormField
          control={form.control}
          name="vlr_produto"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputMask
                  {...field}
                  form="price-list-new-combo-product-form"
                  placeholder="R$"
                  mask="currency"
                  disabled={form.formState.isSubmitting}
                />
              </FormControl>

              <FormMessage className="absolute w-max" />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="w-16 min-w-16 max-w-16">
        <ShowImage
          name={form.getValues('produto_nome')}
          imagePath={currentImagePath ?? undefined}
        />
      </TableCell>

      <TableCell className="w-56 min-w-56 max-w-56">
        <NewComboProductActions onClear={() => setCurrentImagePath(null)} />
      </TableCell>
    </TableRow>
  )
}

export function ComboProductsTable({
  isEmptyComboList,
}: {
  isEmptyComboList: boolean
}) {
  const [sorting, setSorting] = useState<SortingState>([])

  const { comboProducts, setComboProducts } = useUpdateProductContext()

  const form = useForm<FormComboProduct>({
    resolver: zodResolver(FormComboProductSchema),
    defaultValues: getDefaultsFromZod(FormComboProductSchema),
  })

  const columns = useMemo<ColumnDef<ComboProduct>[]>(
    () => [
      {
        accessorKey: 'name',
        header: () => <span>PRODUTO</span>,
        cell: ({
          row: {
            original: {
              produto: { nome },
            },
          },
        }) => (
          <div className="h-10 py-2 pl-3 pr-4">
            <span>{nome}</span>
          </div>
        ),
      },
      {
        accessorKey: 'quantity',
        header: () => <span>QUANTIDADE</span>,
        cell: ({
          row: {
            original: { qtde_produto: quantidade },
          },
        }) => (
          <div className="h-10 py-2 pl-3 pr-4">
            <span>{quantidade > 9 ? quantidade : `0${quantidade}`}</span>
          </div>
        ),
      },
      {
        accessorKey: 'value',
        header: () => <span>VALOR</span>,
        cell: ({
          row: {
            original: { vlr_produto: valor },
          },
        }) => (
          <div className="h-10 py-2 pl-3 pr-4">
            <span>{masks.currency(Number(valor).toFixed(2))}</span>
          </div>
        ),
      },
      {
        accessorKey: 'image',
        header: () => <span>IMAGEM</span>,
        cell: ({
          row: {
            original: {
              produto: { nome: name, imagem_path: imagePath },
            },
          },
        }) => <ShowImage name={name} imagePath={imagePath ?? undefined} />,
      },
      {
        accessorKey: 'actions',
        header: () => <></>,
        cell: ({
          row: {
            original: { produto, cod_produto_combo: id },
          },
        }) => (
          <ComboProductActions
            product={
              id
                ? {
                    ...produto,
                    tipo_ticket_combo: null,
                    produto_combo_list: [],
                  }
                : undefined
            }
            onDeleteComboItem={() =>
              setComboProducts((prev) =>
                prev.filter((item) =>
                  item.cod_produto_combo
                    ? item.cod_produto_combo !== id
                    : item.produto.cod_produto !== produto.cod_produto,
                ),
              )
            }
          />
        ),
      },
    ],
    [setComboProducts],
  )

  const table = useReactTable({
    data: comboProducts,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  })

  function handleSubmit<T>(e: FormEvent<T>) {
    e.preventDefault()

    return form.handleSubmit((data) => {
      setComboProducts((prev) => [
        ...prev,
        {
          cod_produto_combo: 0,
          qtde_produto: Number(data.qtde_produto),
          vlr_produto: parseFloat(formatToNumber(data.vlr_produto)),
          produto: {
            cod_produto: Number(data.produto),
            nome: data.produto_nome,
            imagem_path: data.imagem_path,
          } as Product,
        },
      ])

      form.reset()
    })()
  }

  return (
    <Form {...form}>
      <form id="price-list-new-combo-product-form" onSubmit={handleSubmit} />

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  className="h-12 bg-blue-400 text-gray-25"
                  key={header.id}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          <PriceListNewComboProduct />

          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isEmptyComboList && (
        <FormMessage className="mt-1 w-full">
          Você deve adicionar pelo menos um produto no combo
        </FormMessage>
      )}
    </Form>
  )
}
